import clsx from "clsx";
// eslint-disable-next-line no-restricted-imports
import NextImageImport from "next/image";
import type { ComponentType, FunctionComponent, JSX } from "react";

import { cover } from "./Image.css";
import { defaultLoader, placeholderData } from "./Image.utils";

type StaticImageData = {
  src: string;
  height: number;
  width: number;
  blurDataURL?: string;
  blurWidth?: number;
  blurHeight?: number;
};

type StaticImageImport = StaticImageData | { default: StaticImageData };

type ImageLoader = (props: {
  src: string;
  width: number;
  quality?: number;
}) => string;

type ImageProps = Omit<
  JSX.IntrinsicElements["img"],
  "src" | "srcSet" | "ref" | "alt" | "width" | "height" | "loading"
> & {
  src: string | StaticImageImport;
  alt: string;
  width?: number | `${number}`;
  height?: number | `${number}`;
  fill?: boolean;
  loader?: ImageLoader;
  quality?: number | `${number}`;
  priority?: boolean;
  loading?: "lazy" | "eager";
  placeholder?: "blur" | "empty" | `data:image/${string}`;
  blurDataURL?: string;
  unoptimized?: boolean;
  overrideSrc?: string;
  onLoadingComplete?: (img: HTMLImageElement) => void;
  layout?: string;
  objectFit?: string;
  objectPosition?: string;
  lazyBoundary?: string;
  lazyRoot?: string;
};

// Webpack dev can resolve next/image as nested namespace objects when duet-ui is
// consumed through its ESM dist. Unwrap to the actual Next Image component.
const getNextImageComponent = (
  module: ComponentType<ImageProps> | { default: unknown },
): ComponentType<ImageProps> => {
  let candidate = module as unknown;

  while (
    candidate &&
    typeof candidate === "object" &&
    !("$$typeof" in candidate) &&
    "default" in candidate
  ) {
    candidate = candidate.default;
  }

  return candidate as ComponentType<ImageProps>;
};

const NextImage = getNextImageComponent(
  NextImageImport as ComponentType<ImageProps> | { default: unknown },
);

const Image: FunctionComponent<ImageProps> = ({
  sizes,
  placeholder = "blur",
  blurDataURL = placeholderData,
  className,
  ...props
}) => {
  const fill = props.fill || !(props.height && props.width);

  return (
    <NextImage
      {...props}
      fill={fill}
      className={clsx(className, fill && cover)}
      blurDataURL={blurDataURL}
      placeholder={placeholder}
      sizes={sizes}
      loader={defaultLoader}
      data-chromatic="ignore"
    />
  );
};

export default Image;
